<template>
  <div>
    <!--Policy Change-->
    <div v-if="showPolicy">
      <SectionSeparatorWrapper headerText="Policy changes" class="field" :separator="false" headerRequiredFieldsText="indicates required field">
        <!--Reason for policy change-->
        <FormField labelText="Reason for policy change" errorMessage="Please include a response for Reason for policy change.">
          <DropdownSelect
            :items="getPolicyTerminationReasons"
            v-model="selectedTerminationReasons.policy.reason"
            :initialSelect="getDropdownOption(getPolicyTerminationReasons, selectedTerminationReasons.policy.reason, 'Please select a reason')"
            :noDisabled="false"
          />
        </FormField>
        <!--New fund name-->
        <FormField labelText="New fund name" errorMessage="Please include a response for New fund name." v-if="isFundNameRequired(selectedTerminationReasons.policy.reason)">
          <DropdownSelect
            :items="getFundNames"
            v-model="selectedTerminationReasons.policy.fund"
            :initialSelect="getDropdownOption(getFundNames, selectedTerminationReasons.policy.fund, 'Please select a fund name')"
            :noDisabled="false"
          />
        </FormField>
      </SectionSeparatorWrapper>
    </div>

    <!--Partner-->
    <div v-if="showPartner && partner">
      <SectionSeparatorWrapper :separator="false">
        <h3 class="h6 sub-heading-underlined">Partner</h3>
        <FormField :helperText="referenceData.additionalInformationPolicyChangesSupportText.informationText" :noLabelOverride="true">
          <MemberTag
            :firstName="partner.firstName ? partner.firstName : '&#8212;'"
            :lastName="partner.lastName ? partner.lastName : '&#8212;'"
            relationship="Partner"
            :showLastName="true"
            :disabled="true"
          />
        </FormField>

        <FormField>
          <Alert
            alertType="info"
            class="inline-message"
            :message="'This adult will no longer be covered on this policy. If this cancellation needs to occur on a earlier date, please contact Defence Health.'"
          />

          <!-- If partner holds policy eligibility and is being removed from the policy -->
            <div v-if="showEligibilityAlert" :required="showEligibilityAlert">
              <Alert alertType="info" class="inline-message" message="You are currently removing the member on the policy who holds eligibility." />
              <input
                type="checkbox"
                id="acknowledgedEligibilityChange"
                name="acknowledgedEligibilityChange"
                v-model="acknowledgedEligibilityChange"
                @change="v$.acknowledgedEligibilityChange.$touch()"
                @keyup.enter.space="acknowledgedEligibilityChange = !acknowledgedEligibilityChange"
              />
              <label for="acknowledgedEligibilityChange">I understand that removing the adult changes the eligibility of the remaining policy members.</label>
            </div>
        </FormField>

        <!--Reason for termination-->
        <FormField labelText="Reason for termination" errorMessage="Please include a response for reason.">
          <DropdownSelect
            :items="getMemberTerminationReasons"
            v-model="selectedTerminationReasons.partner.reason"
            :initialSelect="getDropdownOption(getMemberTerminationReasons, selectedTerminationReasons.partner.reason, 'Please select a reason')"
            :noDisabled="false"
          />
        </FormField>
        <!--New fund name-->
        <FormField labelText="New fund name" errorMessage="Please include a response for New fund name." v-if="isFundNameRequired(selectedTerminationReasons.partner.reason)">
          <DropdownSelect
            :items="getFundNames"
            v-model="selectedTerminationReasons.partner.fund"
            :initialSelect="getDropdownOption(getFundNames, selectedTerminationReasons.partner.fund, 'Please select a fund name')"
            :noDisabled="false"
          />
        </FormField>
      </SectionSeparatorWrapper>
    </div>
    <!--Children-->
    <div v-if="showChildren && children.length > 0">
      <SectionSeparatorWrapper v-for="(child, index) in children" :key="child.personCode" :separator="false">
        <h3 class="h6 sub-heading-underlined">{{ `Child ${index + 1}` }}</h3>
        <FormField>
          <MemberTag
            :firstName="child.firstName ? child.firstName : '&#8212;'"
            :lastName="child.lastName ? child.lastName : '&#8212;'"
            relationship="Child"
            :showLastName="true"
            :disabled="true"
          />
        </FormField>

        <FormField>
          <Alert alertType="info" class="inline-message" :message="'This child will no longer be covered on this policy.'" />
        </FormField>

        <!--Reason for termination-->
        <FormField labelText="Reason for termination" errorMessage="Please include a response for reason.">
          <DropdownSelect
            :items="getMemberTerminationReasons"
            v-model="selectedTerminationReasons.children[index].reason"
            :initialSelect="getDropdownOption(getMemberTerminationReasons, selectedTerminationReasons.children[index].reason, 'Please select a reason')"
            :noDisabled="false"
          />
        </FormField>
        <!--New fund name-->
        <FormField
          labelText="New fund name"
          errorMessage="Please include a response for New fund name."
          v-if="isFundNameRequired(selectedTerminationReasons.children[index].reason)"
        >
          <DropdownSelect
            :items="getFundNames"
            v-model="selectedTerminationReasons.children[index].fund"
            :initialSelect="getDropdownOption(getFundNames, selectedTerminationReasons.children[index].fund, 'Please select a fund name')"
            :noDisabled="false"
          />
        </FormField>
      </SectionSeparatorWrapper>
    </div>
  </div>
</template>

<script>
import { requiredIf } from '@vuelidate/validators';
import { Alert, FormField, MemberTag, SectionSeparatorWrapper } from '@/components';
import DropdownSelect from '@general/js/components/DropdownSelect.vue';
import { isFundNameRequired } from '@general/js/utils';
import { sortByObjectProperty } from '@/utils';
import EligibilityTypes from '@/enums/EligibilityTypes';
import { findDependents, findSpouse } from '@/utils/ChangeProduct/ExtraUtils';
import ChangeCoverMixins from '../ChangeCoverMixins';
import CaptureInformationMixins from '../CaptureInformationMixins';
import useVuelidate from '@vuelidate/core';

export default {
  name: 'PolicyChanges',

  setup() {
    return { v$: useVuelidate() };
  },

  mixins: [CaptureInformationMixins, ChangeCoverMixins],

  components: {
    Alert,
    DropdownSelect,
    FormField,
    MemberTag,
    SectionSeparatorWrapper,
  },

  activated() {
    this.isActive = true;
    this.v$.$touch();
  },

  deactivated() {
    this.isActive = false;
  },

  props: {
    fundNamesTerminated: {
      type: Array,
      required: true,
    },
    terminationReasons: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      children: [],
      partner: {},
      selectedTerminationReasons: {
        policy: {},
        partner: {},
        children: [],
      },
      terminatedProductCodes: null,
      isActive: false,
      acknowledgedEligibilityChange: null,
    };
  },

  created() {
    this.fetchPolicyMembers();
  },

  beforeMount() {
    this.selectedTerminationReasons = { ...this.selectedTerminationReasons, ...this.policyChangesStep.selectedTerminationReasons };
    this.acknowledgedEligibilityChange = this.showEligibilityAlert ? false : null;
  },

  methods: {
    isFundNameRequired,

    fetchPolicyMembers() {
      // Get children
      const childrenDetails = findDependents(this.memberDetails);

      // Populate selected options array with number of children
      const selectedChildrenOptions = [];
      childrenDetails.forEach((child) => {
        selectedChildrenOptions.push({
          personCode: child.personCode,
        });
      });

      this.children = childrenDetails;

      if (this.showChildren) {
        // Only add blank object to store termination reasons for children in Children section is shown on UI
        this.selectedTerminationReasons.children = selectedChildrenOptions;
      }

      // get partner details
      const partnerInfo = findSpouse(this.memberDetails);

      if (partnerInfo) {
        this.partner = { ...partnerInfo };
        this.selectedTerminationReasons.partner.personCode = partnerInfo.personCode;
      }
    },
  },

  computed: {
    getPolicyTerminationReasons() {
      const policyTerminationReasonOptions = [...this.terminationReasons.bothReasons];
      return sortByObjectProperty(policyTerminationReasonOptions, 'description').reverse();
    },

    getMemberTerminationReasons() {
      const memberTerminationReasonOptions = [...this.terminationReasons.memberReasons, ...this.terminationReasons.bothReasons];
      return sortByObjectProperty(memberTerminationReasonOptions, 'description').reverse();
    },

    getFundNames() {
      return this.fundNamesTerminated;
    },

    /** Show eligibility alert if:
     *     1. Policy holder is not eligible and
     *     2. Eligibility is curr/ex serving and has not changed
     *        - currently serving -> currently serving or
     *        - ex serving -> ex serving
     *     3. Policy composition has changed from couple to single */
    showEligibilityAlert() {
      const hasServed = [...EligibilityTypes.EX_SERVING, ...EligibilityTypes.SERVING].includes(this.currentEligibility);
      const hasCompositionChangedToSingle = this.showPartner;
      return !this.isPolicyHolderEligible && hasServed && !this.hasEligibilityChanged && hasCompositionChangedToSingle;
    },
  },

  watch: {
    v$: {
      handler(newData) {
        if (this.isActive) {
          this.$nextTick(() => {
            this.$emit('formUpdated', {
              response: { selectedTerminationReasons: this.selectedTerminationReasons, terminatedProductCodes: this.terminatedProductCodes },
              isInvalid: newData.$invalid || newData.$error || (this.showEligibilityAlert && !this.acknowledgedEligibilityChange),
            });
          });
        }
      },
      deep: true,
    },

    selectedTerminationReasons: {
      handler(newValue) {
        // Remove fund name value when termination reason doesn't need a fund name
        if (!this.isFundNameRequired(newValue.policy.reason)) {
          delete this.selectedTerminationReasons.policy.fund;
        }
        if (!this.isFundNameRequired(newValue.partner.reason)) {
          delete this.selectedTerminationReasons.partner.fund;
        }
        newValue.children.forEach((c) => {
          if (!this.isFundNameRequired(c.reason)) {
            delete c.fund;
          }
        });
      },
      deep: true,
      immediate: true,
    },
  },

  validations() {
    const validationsObj = {
      acknowledgedEligibilityChange: {
        required: requiredIf(function () {
          return this.showEligibilityAlert;
        }),
      },
      selectedTerminationReasons: {
        policy: {
          required: requiredIf(() => {
            return this.showPolicy;
          }),
          reason: {
            required: requiredIf(() => {
              return this.showPolicy;
            }),
          },
          fund: {
            required: requiredIf(() => {
              return this.isFundNameRequired(this.selectedTerminationReasons.policy.reason);
            }),
          },
        },
        partner: {
          required: requiredIf(() => {
            return this.showPartner;
          }),
          reason: {
            required: requiredIf(() => {
              return this.showPartner;
            }),
          },
          fund: {
            required: requiredIf(() => {
              return this.isFundNameRequired(this.selectedTerminationReasons.partner.reason);
            }),
          },
        },
        children: {
          required: requiredIf(() => {
            return this.showChildren;
          }),
          $each: {
            reason: {
              required: requiredIf(() => {
                return this.showChildren;
              }),
            },
            fund: {
              required: requiredIf((obj) => {
                return this.isFundNameRequired(obj.reason);
              }),
            },
          },
        },
      },
    };
    return validationsObj;
  },
};
</script>

<style lang="scss" scoped>
.name-row {
  display: flex;
  .name-col {
    flex-basis: 100%;
    flex: 1;
  }
}

.dropdown-select-wrapper {
  overflow: visible;
}
</style>
